/**
 * DEACTIVATION REVIEW ACTIONS COMPONENT
 *
 * Approve or disapprove location deactivation request
 */

'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Field,
  FieldLabel,
  FieldSet,
  FieldDescription,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle } from 'lucide-react';
import type { LocationDeactivationQueue } from '@/types/database';
import { updateDeactivationStatus } from '@/lib/onboarding-status';

interface DeactivationReviewActionsProps {
  deactivationRequest: LocationDeactivationQueue;
  userId: string;
}

export function DeactivationReviewActions({
  deactivationRequest,
  userId,
}: DeactivationReviewActionsProps) {
  const [approveNotes, setApproveNotes] = useState('');
  const [disapprovalReason, setDisapprovalReason] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [disapproveLoading, setDisapproveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const supabase = createClient();

  const handleApprove = async () => {
    try {
      setApproveLoading(true);
      setError(null);

      // Update location_deactivation_queue status to APPROVED
      const { error: updateError } = await supabase
        .from('location_deactivation_queue')
        .update({
          status: 'APPROVED',
          approved_by: userId,
          approved_at: new Date().toISOString(),
          review_notes: approveNotes || null,
        })
        .eq('id', deactivationRequest.id);

      if (updateError) {
        console.error('Deactivation approval update error:', updateError);
        throw updateError;
      }

      // Update deactivation status in main app to APPROVED
      // This will trigger subscription end date update in the main app
      try {
        await updateDeactivationStatus({
          organization_id: deactivationRequest.organization_id,
          locations: [
            {
              location_id: deactivationRequest.location_id,
              status: 'APPROVED',
              approved_by: userId,
            },
          ],
        });
      } catch (statusError) {
        console.error('Failed to update deactivation status in main app:', statusError);
        // Continue even if main app update fails
      }

      navigate('/review');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to approve deactivation'
      );
      setApproveLoading(false);
    }
  };

  const handleDisapprove = async () => {
    if (!disapprovalReason.trim()) {
      setError('Please provide a disapproval reason');
      return;
    }

    try {
      setDisapproveLoading(true);
      setError(null);

      // Update location_deactivation_queue status to DISAPPROVED
      const { error: updateError } = await supabase
        .from('location_deactivation_queue')
        .update({
          status: 'DISAPPROVED',
          disapproved_by: userId,
          disapproved_at: new Date().toISOString(),
          disapproval_reason: disapprovalReason,
        })
        .eq('id', deactivationRequest.id);

      if (updateError) {
        console.error('Deactivation disapproval update error:', updateError);
        throw updateError;
      }

      // Update deactivation status in main app to DISAPPROVED
      try {
        await updateDeactivationStatus({
          organization_id: deactivationRequest.organization_id,
          locations: [
            {
              location_id: deactivationRequest.location_id,
              status: 'DISAPPROVED',
              disapproved_by: userId,
              disapproval_reason: disapprovalReason,
            },
          ],
        });
      } catch (statusError) {
        console.error('Failed to update deactivation status in main app:', statusError);
        // Continue even if main app update fails
      }

      // Update the review task status to COMPLETED
      await supabase
        .from('tasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          completed_by: userId,
        })
        .eq('organization_id', deactivationRequest.organization_id)
        .eq('location_id', deactivationRequest.location_id)
        .eq('type', 'DEACTIVATION_REVIEW');

      navigate('/review');
    } catch (err) {
      console.error('Disapprove error:', err);
      setError(err instanceof Error ? err.message : 'Failed to disapprove');
      setDisapproveLoading(false);
    }
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
      {/* Approve */}
      <Card className='border-green-200 dark:border-green-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-green-700 dark:text-green-400'>
            <CheckCircle className='w-5 h-5' />
            Approve Deactivation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldSet className='space-y-4'>
            <Field>
              <FieldLabel>Review Notes (Optional)</FieldLabel>
              <Input
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder='Add any notes about this approval...'
                disabled={approveLoading}
              />
              <FieldDescription>
                These notes will be saved for internal reference
              </FieldDescription>
            </Field>

            {error && !disapproveLoading && (
              <div className='text-sm text-red-600 dark:text-red-400'>
                {error}
              </div>
            )}

            <Button
              onClick={handleApprove}
              disabled={approveLoading || disapproveLoading}
              className='w-full bg-green-600 hover:bg-green-700'
            >
              {approveLoading
                ? 'Approving...'
                : 'Approve Deactivation'}
            </Button>
            
            <p className='text-xs text-muted-foreground'>
              This will update the subscription to end on the last billing date. Deconfig tasks will be created when subscription ends.
            </p>
          </FieldSet>
        </CardContent>
      </Card>

      {/* Disapprove */}
      <Card className='border-red-200 dark:border-red-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-red-700 dark:text-red-400'>
            <XCircle className='w-5 h-5' />
            Disapprove Deactivation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldSet className='space-y-4'>
            <Field>
              <FieldLabel>Disapproval Reason (Required)</FieldLabel>
              <Input
                value={disapprovalReason}
                onChange={(e) => setDisapprovalReason(e.target.value)}
                placeholder='Provide a reason for disapproval...'
                disabled={disapproveLoading}
                required
              />
              <FieldDescription>
                This reason will be sent to the client
              </FieldDescription>
            </Field>

            {error && disapproveLoading && (
              <div className='text-sm text-red-600 dark:text-red-400'>
                {error}
              </div>
            )}

            <Button
              onClick={handleDisapprove}
              disabled={!disapprovalReason.trim() || disapproveLoading}
              variant='destructive'
              className='w-full'
            >
              {disapproveLoading ? 'Disapproving...' : 'Disapprove Deactivation'}
            </Button>
            
            <p className='text-xs text-muted-foreground'>
              Location will remain active and deactivation request will be cancelled
            </p>
          </FieldSet>
        </CardContent>
      </Card>
    </div>
  );
}
